import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { 
  Calendar, LayoutGrid, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Maximize2, Minimize2, Search, Filter, Plus, X, AlertTriangle, 
  Truck, DollarSign, Users, Clock, MapPin, GripVertical, Wrench, Scissors, Settings2, Gauge, Eye
} from 'lucide-react';
import { formatDate, formatDateForInput, formatDisplayDate } from '@/utils/dateUtils';
import EquipmentGanttView from '@/components/EquipmentGanttView';
import { useBulkAllocate, BulkAllocateBar, BatchAllocateModal } from '@/components/BulkAllocate';
import ProjectDashboard from '@/components/ProjectDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function EquipmentPlanner({ user }) {
  const notification = useNotification();
  const { canEdit: canEditModule } = usePermissions();
  const canEdit = canEditModule('equipment_planner');
  const containerRef = useRef(null);
  const ganttContainerRef = useRef(null);
  
  // Data state
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allocations, setAllocations] = useState([]);
  
  // View state
  const [viewType, setViewType] = useState('project'); // 'project' or 'resource'
  const [timeScale, setTimeScale] = useState('weekly'); // 'daily', 'weekly', 'monthly'
  const [planMode, setPlanMode] = useState('actual'); // 'actual' or 'forecast'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showGanttView, setShowGanttView] = useState(false);
  
  // Panel state
  const [selectedProject, setSelectedProject] = useState(null);
  const [dashboardProjectId, setDashboardProjectId] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [resourceFilter, setResourceFilter] = useState('');
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  
  // Project filter state
  const [projectFilter, setProjectFilter] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState('Secured'); // default to Secured for Actual mode
  
  // Drag and drop state
  const [draggedEquipment, setDraggedEquipment] = useState(null);
  const [dropTargetProject, setDropTargetProject] = useState(null);
  
  // Resize state for Gantt bars
  const [resizing, setResizing] = useState(null); // { allocationId, edge: 'start' | 'end', initialX, initialDate }
  const [didResize, setDidResize] = useState(false); // Track if resize occurred to prevent click
  
  // Slide state for Gantt bars (middle-drag to move)
  const [sliding, setSliding] = useState(null); // { allocationId, initialX, initialStart, initialEnd }
  const [didSlide, setDidSlide] = useState(false);

  // Multi-select state
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState(new Set());
  
  // Category collapse state
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Split allocation modal state
  const [splitModal, setSplitModal] = useState(null); // { allocationId, equipmentItem, project, splitDate }

  // Batch drop modal state is managed by the shared useBulkAllocate hook (see below).

  // Conflicts modal
  const [showConflictsModal, setShowConflictsModal] = useState(false);

  // Customizable left panel columns
  const COLUMN_OPTIONS = [
    { key: 'subcategory', label: 'Subcategory' },
    { key: 'category', label: 'Category' },
    { key: 'location', label: 'Location' },
    { key: 'maintenance_status', label: 'Maintenance' },
    { key: 'serial_number', label: 'Serial #' },
  ];
  const [extraColumns, setExtraColumns] = useState(() => {
    try { return JSON.parse(localStorage.getItem('eq_planner_columns')) || ['subcategory']; }
    catch { return ['subcategory']; }
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const middlePanelRef = useRef(null);
  
  // Allocation form
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [allocationForm, setAllocationForm] = useState({
    project_id: '',
    start_date: '',
    end_date: '',
    status: 'active',
    is_override: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-expand projects on first load
  useEffect(() => {
    if (projects.length > 0 && Object.keys(expandedProjects).length === 0) {
      const expanded = {};
      projects.forEach(p => { expanded[p.id] = true; });
      setExpandedProjects(expanded);
    }
  }, [projects]);

  const fetchData = async () => {
    try {
      const [equipmentRes, projectsRes, allocationsRes, conflictsRes] = await Promise.all([
        axios.get(`${API}/equipment`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/equipment`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/conflicts`, { headers: getAuthHeader() }).catch(() => ({ data: { equipment_conflicts: [] } }))
      ]);
      setEquipment(equipmentRes.data);
      setProjects(projectsRes.data);
      setAllocations(allocationsRes.data);
      setConflicts(conflictsRes.data?.equipment_conflicts || []);
    } catch (error) {
      notification.error('Failed to load planner data');
    }
  };

  // Helper functions
  const getProjectColor = (status) => {
    const colors = {
      'Secured': 'bg-emerald-500',
      'Likely': 'bg-amber-500',
      'Prospect': 'bg-blue-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  const getProjectBarStyle = (status) => {
    if (status === 'Secured') return {};
    // Striped pattern for Likely and Prospect (forecasted)
    const color = status === 'Likely' ? 'rgba(245, 158, 11, 0.85)' : 'rgba(59, 130, 246, 0.85)';
    const stripe = status === 'Likely' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(59, 130, 246, 0.5)';
    return {
      background: `repeating-linear-gradient(135deg, ${color}, ${color} 4px, ${stripe} 4px, ${stripe} 8px)`
    };
  };

  const getProjectBorderColor = (status) => {
    const colors = {
      'Secured': 'border-emerald-500',
      'Likely': 'border-amber-500',
      'Prospect': 'border-blue-500'
    };
    return colors[status] || 'border-slate-500';
  };

  const getEquipmentAllocations = (equipmentId) => {
    return allocations.filter(a => a.equipment_id === equipmentId && a.status === 'active');
  };

  const getProjectAllocations = (projectId) => {
    return allocations.filter(a => a.project_id === projectId && a.status === 'active');
  };

  const getEquipmentById = (id) => equipment.find(e => e.id === id);

  const isEquipmentAllocated = (equipmentId, startDate, endDate) => {
    const allocs = getEquipmentAllocations(equipmentId);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return allocs.some(a => {
      const aStart = new Date(a.start_date);
      const aEnd = new Date(a.end_date);
      return start <= aEnd && end >= aStart;
    });
  };

  const isEquipmentAvailable = (equipmentId) => {
    const allocs = getEquipmentAllocations(equipmentId);
    const now = new Date();
    return !allocs.some(a => {
      const aStart = new Date(a.start_date);
      const aEnd = new Date(a.end_date);
      return now >= aStart && now <= aEnd;
    });
  };

  // Timeline generation
  const generateTimeline = () => {
    const timeline = [];
    const start = new Date(currentDate);
    
    if (timeScale === 'daily') {
      start.setDate(start.getDate() - start.getDay()); // Start from Sunday
      for (let i = 0; i < 14; i++) { // 2 weeks
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        timeline.push({ date, label: date.getDate().toString(), subLabel: date.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', year: '2-digit' }) });
      }
    } else if (timeScale === 'weekly') {
      start.setDate(1); // Start of month
      const numWeeks = 12; // 3 months of weeks
      for (let i = 0; i < numWeeks; i++) {
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() + (i * 7));
        timeline.push({ 
          date: weekStart, 
          label: `W${Math.ceil(weekStart.getDate() / 7)}`,
          subLabel: weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
        });
      }
    } else { // monthly
      start.setDate(1);
      for (let i = 0; i < 6; i++) { // 6 months
        const month = new Date(start);
        month.setMonth(start.getMonth() + i);
        timeline.push({ 
          date: month, 
          label: month.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
          subLabel: month.getFullYear().toString()
        });
      }
    }
    return timeline;
  };

  const timeline = generateTimeline();

  // Calculate bar position
  const getBarStyle = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timelineStart = timeline[0]?.date || new Date();
    const timelineEnd = timeline[timeline.length - 1]?.date || new Date();
    
    // Adjust timeline end based on scale
    const adjustedEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedEnd.setDate(adjustedEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedEnd.setDate(adjustedEnd.getDate() + 7);
    else adjustedEnd.setMonth(adjustedEnd.getMonth() + 1);
    
    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    
    const barStart = Math.max(0, (start - timelineStart) / (1000 * 60 * 60 * 24));
    const barEnd = Math.min(totalDays, (end - timelineStart) / (1000 * 60 * 60 * 24));
    
    if (barEnd < 0 || barStart > totalDays) return null;
    
    const left = (barStart / totalDays) * 100;
    const width = ((barEnd - barStart) / totalDays) * 100;
    
    return { left: `${Math.max(0, left)}%`, width: `${Math.min(100 - left, Math.max(2, width))}%` };
  };

  // Calculate maintenance overlay position within the VISIBLE portion of the bar
  const getMaintenanceOverlay = (equipmentItem, allocStartDate, allocEndDate) => {
    if (!equipmentItem?.next_maintenance_date) return null;
    
    const maintDate = new Date(equipmentItem.next_maintenance_date);
    const allocStart = new Date(allocStartDate);
    const allocEnd = new Date(allocEndDate);
    
    // Check if maintenance falls within allocation period
    if (maintDate < allocStart || maintDate > allocEnd) return null;
    
    // Get timeline bounds
    const timelineStart = timeline[0]?.date || new Date();
    const timelineEnd = timeline[timeline.length - 1]?.date || new Date();
    const adjustedTimelineEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedTimelineEnd.setDate(adjustedTimelineEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedTimelineEnd.setDate(adjustedTimelineEnd.getDate() + 7);
    else adjustedTimelineEnd.setMonth(adjustedTimelineEnd.getMonth() + 1);
    
    // Calculate visible bar bounds (clipped to timeline)
    const visibleBarStart = new Date(Math.max(allocStart.getTime(), timelineStart.getTime()));
    const visibleBarEnd = new Date(Math.min(allocEnd.getTime(), adjustedTimelineEnd.getTime()));
    
    // Check if maintenance falls within VISIBLE portion of the bar
    if (maintDate < visibleBarStart || maintDate > visibleBarEnd) return null;
    
    // Calculate position within the VISIBLE bar (as percentage)
    const visibleBarDays = (visibleBarEnd - visibleBarStart) / (1000 * 60 * 60 * 24);
    const maintDayOffset = (maintDate - visibleBarStart) / (1000 * 60 * 60 * 24);
    
    if (visibleBarDays <= 0) return null;
    
    const left = (maintDayOffset / visibleBarDays) * 100;
    // Width for maintenance marker - at least 1 day or 5% of bar, max 15%
    const width = Math.min(15, Math.max(5, (1 / visibleBarDays) * 100));
    
    return { left: `${Math.max(0, Math.min(95, left))}%`, width: `${Math.min(100 - left, width)}%` };
  };

  // Get equipment status dot color
  const getEquipmentDotColor = (equipmentItem) => {
    const maintenanceStatus = equipmentItem?.maintenance_status;
    
    if (maintenanceStatus === 'Maintenance') {
      return 'bg-orange-500'; // Orange for Maintenance
    }
    if (maintenanceStatus === 'Out Of Service' || maintenanceStatus === 'Out of Service') {
      return 'bg-black'; // Black for Out of Service
    }
    
    // Default: check availability
    const isAvailable = isEquipmentAvailable(equipmentItem.id);
    return isAvailable ? 'bg-emerald-500' : 'bg-slate-300';
  };

  // Get equipment status tooltip
  const getEquipmentStatusTooltip = (equipmentItem) => {
    const maintenanceStatus = equipmentItem?.maintenance_status;
    
    if (maintenanceStatus === 'Maintenance') {
      return 'In Maintenance';
    }
    if (maintenanceStatus === 'Out Of Service' || maintenanceStatus === 'Out of Service') {
      return 'Out of Service';
    }
    
    const isAvailable = isEquipmentAvailable(equipmentItem.id);
    return isAvailable ? 'Available' : 'Allocated';
  };

  // Resize handlers for Gantt bars
  const handleResizeStart = (e, allocationId, edge, currentDate) => {
    if (!canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    setDidResize(false);
    setResizing({
      allocationId,
      edge,
      initialX: e.clientX,
      initialDate: new Date(currentDate)
    });
  };

  const handleResizeMove = (e) => {
    if (!resizing || !ganttContainerRef.current) return;
    
    const containerRect = ganttContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const deltaX = e.clientX - resizing.initialX;
    
    // Calculate days moved based on timeline
    const timelineStart = timeline[0]?.date || new Date();
    const timelineEnd = timeline[timeline.length - 1]?.date || new Date();
    const adjustedEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedEnd.setDate(adjustedEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedEnd.setDate(adjustedEnd.getDate() + 7);
    else adjustedEnd.setMonth(adjustedEnd.getMonth() + 1);
    
    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const daysPerPixel = totalDays / containerWidth;
    const daysMoved = Math.round(deltaX * daysPerPixel);
    
    if (daysMoved === 0) return;
    
    setDidResize(true); // Mark that a resize occurred
    
    // Update allocation dates
    setAllocations(prev => prev.map(alloc => {
      if (alloc.id !== resizing.allocationId) return alloc;
      
      const newAlloc = { ...alloc };
      if (resizing.edge === 'start') {
        const newStart = new Date(resizing.initialDate);
        newStart.setDate(newStart.getDate() + daysMoved);
        // Don't let start go past end
        if (newStart < new Date(alloc.end_date)) {
          newAlloc.start_date = newStart.toISOString();
        }
      } else {
        const newEnd = new Date(resizing.initialDate);
        newEnd.setDate(newEnd.getDate() + daysMoved);
        // Don't let end go before start
        if (newEnd > new Date(alloc.start_date)) {
          newAlloc.end_date = newEnd.toISOString();
        }
      }
      return newAlloc;
    }));
  };

  const handleResizeEnd = async () => {
    if (!resizing) return;
    
    const alloc = allocations.find(a => a.id === resizing.allocationId);
    if (alloc && didResize) {
      try {
        await axios.put(`${API}/allocations/equipment/${alloc.id}`, {
          equipment_id: alloc.equipment_id,
          project_id: alloc.project_id,
          start_date: alloc.start_date,
          end_date: alloc.end_date,
          status: alloc.status,
          version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation updated');
        fetchData(); // Refresh to get updated version
      } catch (error) {
        notification.error('Failed to update allocation');
        fetchData(); // Revert to server state
      }
    }
    setResizing(null);
    // Keep didResize true briefly to prevent click handler
    setTimeout(() => setDidResize(false), 100);
  };

  // Handle bar click - only open form if not resizing or sliding
  const handleBarClick = (e, eq, alloc) => {
    e.stopPropagation();
    if (!canEdit) return;
    if (didResize || resizing || didSlide || sliding) return;
    handleEditAllocation(eq, alloc);
  };

  // Slide handlers (middle-drag to move entire bar)
  const handleSlideStart = (e, allocationId) => {
    if (!canEdit) return;
    if (resizing) return;
    e.stopPropagation();
    e.preventDefault();
    const alloc = allocations.find(a => a.id === allocationId);
    if (!alloc) return;
    setDidSlide(false);
    setSliding({
      allocationId,
      initialX: e.clientX,
      initialStart: new Date(alloc.start_date),
      initialEnd: new Date(alloc.end_date)
    });
  };

  const handleSlideMove = (e) => {
    if (!sliding || !ganttContainerRef.current) return;
    
    const containerRect = ganttContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const deltaX = e.clientX - sliding.initialX;
    
    const timelineStart = timeline[0]?.date || new Date();
    const timelineEnd = timeline[timeline.length - 1]?.date || new Date();
    const adjustedEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedEnd.setDate(adjustedEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedEnd.setDate(adjustedEnd.getDate() + 7);
    else adjustedEnd.setMonth(adjustedEnd.getMonth() + 1);
    
    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const daysPerPixel = totalDays / containerWidth;
    const daysMoved = Math.round(deltaX * daysPerPixel);
    
    if (daysMoved === 0) return;
    setDidSlide(true);
    
    setAllocations(prev => prev.map(alloc => {
      if (alloc.id !== sliding.allocationId) return alloc;
      const newStart = new Date(sliding.initialStart);
      newStart.setDate(newStart.getDate() + daysMoved);
      const newEnd = new Date(sliding.initialEnd);
      newEnd.setDate(newEnd.getDate() + daysMoved);
      return { ...alloc, start_date: newStart.toISOString(), end_date: newEnd.toISOString() };
    }));
  };

  const handleSlideEnd = async () => {
    if (!sliding) return;
    const alloc = allocations.find(a => a.id === sliding.allocationId);
    if (alloc && didSlide) {
      try {
        await axios.put(`${API}/allocations/equipment/${alloc.id}`, {
          equipment_id: alloc.equipment_id,
          project_id: alloc.project_id,
          start_date: alloc.start_date,
          end_date: alloc.end_date,
          status: alloc.status,
          version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation moved');
        fetchData();
      } catch (error) {
        notification.error('Failed to move allocation');
        fetchData();
      }
    }
    setSliding(null);
    setTimeout(() => setDidSlide(false), 100);
  };

  // Add mouse event listeners for resize and slide
  useEffect(() => {
    if (resizing || sliding) {
      const handleMouseMove = (e) => {
        if (resizing) handleResizeMove(e);
        if (sliding) handleSlideMove(e);
      };
      const handleMouseUp = () => {
        if (resizing) handleResizeEnd();
        if (sliding) handleSlideEnd();
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing, sliding, allocations, timeline, timeScale]);

  // Navigation
  const navigateTime = (direction) => {
    const newDate = new Date(currentDate);
    if (timeScale === 'daily') newDate.setDate(newDate.getDate() + (direction * 7));
    else if (timeScale === 'weekly') newDate.setMonth(newDate.getMonth() + direction);
    else newDate.setMonth(newDate.getMonth() + (direction * 3));
    setCurrentDate(newDate);
  };

  // Shift+Scroll for horizontal timeline navigation
  useEffect(() => {
    const panel = middlePanelRef.current;
    if (!panel) return;
    const handleWheel = (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        navigateTime(e.deltaY > 0 ? 1 : -1);
      }
    };
    panel.addEventListener('wheel', handleWheel, { passive: false });
    return () => panel.removeEventListener('wheel', handleWheel);
  }, [currentDate, timeScale]);

  // Split allocation handler
  const handleSplitAllocation = async () => {
    if (!canEdit) return;
    if (!splitModal?.splitDate || !splitModal?.allocationId) return;
    const alloc = allocations.find(a => a.id === splitModal.allocationId);
    if (!alloc) return;
    const splitDate = new Date(splitModal.splitDate);
    const origStart = new Date(alloc.start_date);
    const origEnd = new Date(alloc.end_date);
    if (splitDate <= origStart || splitDate >= origEnd) {
      notification.error('Split date must be between start and end dates');
      return;
    }
    try {
      // Delete original
      await axios.delete(`${API}/allocations/equipment/${alloc.id}`, { headers: getAuthHeader() });
      // Create first half (original start → split date - 1 day)
      const firstEnd = new Date(splitDate);
      firstEnd.setDate(firstEnd.getDate() - 1);
      await axios.post(`${API}/allocations/equipment`, {
        equipment_id: alloc.equipment_id, project_id: alloc.project_id,
        start_date: origStart.toISOString(), end_date: firstEnd.toISOString(), status: 'active'
      }, { headers: getAuthHeader() });
      // Create second half (split date → original end)
      await axios.post(`${API}/allocations/equipment`, {
        equipment_id: alloc.equipment_id, project_id: alloc.project_id,
        start_date: splitDate.toISOString(), end_date: origEnd.toISOString(), status: 'active'
      }, { headers: getAuthHeader() });
      notification.success('Allocation split successfully');
      setSplitModal(null);
      fetchData();
    } catch (error) {
      notification.error('Failed to split allocation');
    }
  };

  // Save extra columns preference
  const toggleExtraColumn = (key) => {
    setExtraColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key].slice(0, 2);
      localStorage.setItem('eq_planner_columns', JSON.stringify(next));
      return next;
    });
  };

  // Batch allocation is handled by the shared useBulkAllocate hook (declared before the return).

  // Full screen toggle
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Toggle project expansion
  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // Allocation handlers
  const handleAddAllocation = (equipmentItem, projectId = null) => {
    setSelectedEquipment(equipmentItem);
    setAllocationForm({
      allocation_id: null,
      project_id: projectId || selectedProject?.id || '',
      start_date: formatDateForInput(new Date()),
      end_date: formatDateForInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      status: 'active',
      version: null
    });
    setEditSheetOpen(true);
  };

  const handleEditAllocation = (equipmentItem, allocation) => {
    if (!canEdit) return;
    setSelectedEquipment(equipmentItem);
    setAllocationForm({
      allocation_id: allocation.id,
      project_id: allocation.project_id,
      start_date: formatDateForInput(allocation.start_date),
      end_date: formatDateForInput(allocation.end_date),
      status: allocation.status,
      version: allocation.version || null
    });
    setEditSheetOpen(true);
  };

  const handleSaveAllocation = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    try {
      const data = {
        equipment_id: selectedEquipment.id,
        project_id: allocationForm.project_id,
        start_date: new Date(allocationForm.start_date).toISOString(),
        end_date: new Date(allocationForm.end_date).toISOString(),
        status: allocationForm.status,
        is_override: !!allocationForm.is_override,
        version: allocationForm.version || null
      };

      if (allocationForm.allocation_id) {
        await axios.put(`${API}/allocations/equipment/${allocationForm.allocation_id}`, data, { headers: getAuthHeader() });
        notification.success('Allocation updated');
      } else {
        await axios.post(`${API}/allocations/equipment`, data, { headers: getAuthHeader() });
        notification.success('Allocation created');
      }
      setEditSheetOpen(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        notification.conflict(() => { setEditSheetOpen(false); fetchData(); });
      } else {
        notification.error(error.response?.data?.detail || 'Operation failed');
      }
    }
  };

  const handleDeleteAllocation = () => {
    if (!canEdit) return;
    notification.confirm('Remove this allocation?', async () => {
      try {
        await axios.delete(`${API}/allocations/equipment/${allocationForm.allocation_id}`, { headers: getAuthHeader() });
        notification.success('Allocation removed');
        setEditSheetOpen(false);
        fetchData();
      } catch (error) {
        notification.error('Failed to remove allocation');
      }
    });
  };

  // Quick delete from bar
  const handleQuickDeleteAllocation = (allocId, e) => {
    e.stopPropagation();
    if (!canEdit) return;
    notification.confirm('Remove this allocation?', async () => {
      try {
        await axios.delete(`${API}/allocations/equipment/${allocId}`, { headers: getAuthHeader() });
        notification.success('Allocation removed');
        fetchData();
      } catch (error) {
        notification.error('Failed to remove allocation');
      }
    });
  };

  // Drag and drop handlers (supports multi-select)
  const handleDragStart = (e, equipmentItem) => {
    if (!canEdit) return;
    setDraggedEquipment(equipmentItem);
    e.dataTransfer.effectAllowed = 'move';
    // If multi-selected, always pass all selected IDs
    const ids = selectedEquipmentIds.size > 1
      ? Array.from(selectedEquipmentIds)
      : [equipmentItem.id];
    e.dataTransfer.setData('text/plain', JSON.stringify(ids));
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedEquipment(null);
    setDropTargetProject(null);
  };

  const handleDragOver = (e, projectId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetProject(projectId);
  };

  const handleDragLeave = (e) => {
    setDropTargetProject(null);
  };

  // Check if equipment matches project requirements
  const checkEquipmentRequirement = (eq, project) => {
    const forecasts = project.equipment_forecasts || [];
    if (forecasts.length === 0) return { required: false, message: `No equipment requirements defined for "${project.name}". This equipment is ${eq.category}/${eq.subcategory}.` };
    const match = forecasts.find(f => 
      f.category === eq.category && (!f.subcategory || f.subcategory === eq.subcategory)
    );
    if (match) return { required: true };
    return { required: false, message: `"${project.name}" requires: ${forecasts.map(f => f.subcategory ? `${f.category}/${f.subcategory}` : f.category).join(', ')}. This equipment is ${eq.category}/${eq.subcategory}.` };
  };

  const handleDrop = async (e, project) => {
    e.preventDefault();
    setDropTargetProject(null);
    if (!canEdit) return;
    
    // Parse dropped IDs (may be multi-select)
    let droppedIds = [];
    try {
      droppedIds = JSON.parse(e.dataTransfer.getData('text/plain'));
    } catch {
      droppedIds = draggedEquipment ? [draggedEquipment.id] : [];
    }
    
    if (droppedIds.length === 0) return;

    // Validate requirements for all dropped items
    const nonMatching = [];
    for (const eqId of droppedIds) {
      const eq = equipment.find(e => e.id === eqId);
      if (eq) {
        const check = checkEquipmentRequirement(eq, project);
        if (!check.required) nonMatching.push({ eq, message: check.message });
      }
    }

    const proceedWithDrop = async (isOverride = false) => {
      if (droppedIds.length === 1) {
        const eq = equipment.find(e => e.id === droppedIds[0]);
        if (eq) {
          setSelectedEquipment(eq);
          const startDate = new Date(project.start_date) > new Date() ? project.start_date : new Date();
          setAllocationForm({
            allocation_id: null,
            project_id: project.id,
            start_date: formatDateForInput(startDate),
            end_date: formatDateForInput(project.end_date),
            status: 'active',
            is_override: isOverride,
            version: null
          });
          setEditSheetOpen(true);
        }
      } else {
        // Open batch modal with date picker
        const defaultStart = new Date(project.start_date) > new Date() ? project.start_date : new Date().toISOString();
        const defaultEnd = new Date(project.end_date) > new Date() ? project.end_date : new Date(new Date().getTime() + 30 * 86400000).toISOString();
        bulk.setModal({
          droppedIds,
          project,
          startDate: formatDateForInput(defaultStart),
          endDate: formatDateForInput(defaultEnd),
        });
      }
    };

    if (nonMatching.length > 0) {
      notification.confirm(
        `${nonMatching[0].message}\n\nThis category is not listed in project requirements.\nProceed and flag this allocation as an Override?`,
        () => proceedWithDrop(true),
        {
          title: 'Override Required',
          confirmLabel: 'Override',
          confirmClass: 'bg-orange-500 hover:bg-orange-600',
          iconColor: 'text-orange-500'
        }
      );
    } else {
      proceedWithDrop(false);
    }
    setDraggedEquipment(null);
  };

  // Multi-select equipment (Ctrl/Cmd-click)
  const handleEquipmentClick = (e, equipmentItem) => {
    if (e.ctrlKey || e.metaKey) {
      // Toggle selection
      setSelectedEquipmentIds(prev => {
        const next = new Set(prev);
        if (next.has(equipmentItem.id)) next.delete(equipmentItem.id);
        else next.add(equipmentItem.id);
        return next;
      });
    } else if (e.shiftKey && selectedEquipmentIds.size > 0) {
      // Shift-click: select range within same category
      const allItems = Object.values(groupedEquipment).flat();
      const lastSelected = allItems.findIndex(e => selectedEquipmentIds.has(e.id));
      const current = allItems.findIndex(e => e.id === equipmentItem.id);
      if (lastSelected >= 0 && current >= 0) {
        const [from, to] = [Math.min(lastSelected, current), Math.max(lastSelected, current)];
        const next = new Set(selectedEquipmentIds);
        for (let i = from; i <= to; i++) next.add(allItems[i].id);
        setSelectedEquipmentIds(next);
      }
    } else {
      // Normal click - open allocation form (single)
      setSelectedEquipmentIds(new Set());
      handleAddAllocation(equipmentItem);
    }
  };

  // Collapse All / Expand All categories
  const toggleAllCategories = (collapse) => {
    const newState = {};
    Object.keys(groupedEquipment).forEach(cat => { newState[cat] = collapse; });
    setCollapsedCategories(newState);
  };

  // Filter equipment for left panel
  const filteredEquipment = equipment.filter(e => {
    const matchesSearch = !resourceFilter || 
      e.internal_tag?.toLowerCase().includes(resourceFilter.toLowerCase()) ||
      e.serial_number?.toLowerCase().includes(resourceFilter.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || e.category === categoryFilter;
    const matchesSubcategory = subcategoryFilter === 'all' || e.subcategory === subcategoryFilter;
    const matchesAvailable = !showAvailableOnly || isEquipmentAvailable(e.id);
    return matchesSearch && matchesCategory && matchesSubcategory && matchesAvailable;
  });

  // Distinct categories and subcategories for filter dropdowns
  const categoryOptions = useMemo(() => [...new Set(equipment.map(e => e.category).filter(Boolean))].sort(), [equipment]);
  const subcategoryOptions = useMemo(() => {
    const filtered = categoryFilter === 'all' ? equipment : equipment.filter(e => e.category === categoryFilter);
    return [...new Set(filtered.map(e => e.subcategory).filter(Boolean))].sort();
  }, [equipment, categoryFilter]);

  // Group equipment by category for left panel
  const groupedEquipment = filteredEquipment.reduce((acc, eq) => {
    const cat = eq.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(eq);
    return acc;
  }, {});

  // Filter and sort projects — status filter applies in both modes
  const activeProjects = projects
    .filter(p => {
      const matchesName = !projectFilter || p.name.toLowerCase().includes(projectFilter.toLowerCase());
      const matchesStatus = projectStatusFilter === 'all' ? true : projectStatusFilter === 'forecast' ? (p.status === 'Likely' || p.status === 'Prospect') : p.status === projectStatusFilter;
      return matchesName && matchesStatus;
    })
    .sort((a, b) => {
      const statusOrder = { 'Secured': 0, 'Likely': 1, 'Prospect': 2 };
      return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
    });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(value);
  };

  // Shared bulk-allocate hook (owns the batch modal + open/confirm logic)
  const bulk = useBulkAllocate({
    resource: 'equipment',
    items: equipment,
    checkRequirement: checkEquipmentRequirement,
    createAllocation: (eqId, eq, { projectId, startDate, endDate, isOverride }) =>
      axios.post(`${API}/allocations/equipment`, {
        equipment_id: eqId,
        project_id: projectId,
        start_date: startDate,
        end_date: endDate,
        status: 'active',
        is_override: isOverride,
        version: null,
      }, { headers: getAuthHeader() }),
    onAllocated: fetchData,
    clearSelection: () => setSelectedEquipmentIds(new Set()),
    notification,
  });

  return (
    <div 
      ref={containerRef}
      className={`${isFullScreen ? 'bg-slate-50' : ''} h-full`}
      data-testid="equipment-planner-page"
    >
      {/* Header */}
      <div className={`${isFullScreen ? 'px-6 pt-4' : 'p-6 md:p-8 lg:px-12 lg:pt-8 lg:pb-4'}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Equipment Planner</h1>
            <p className="text-sm text-slate-500">
              {viewType === 'project' ? 'Project-Centric View' : 'Resource-Centric View'} • {timeScale.charAt(0).toUpperCase() + timeScale.slice(1)} Timeline
            </p>
            {!canEdit && (
              <span
                className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium"
                data-testid="eq-planner-view-only-chip"
                title="Your role has view-only access to the Equipment Planner. Editing controls are hidden."
              >
                <Eye className="w-3.5 h-3.5" />
                View only — editing is disabled for your role
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Gantt Overview Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGanttView(true)}
              className="h-9 px-3"
              data-testid="eq-planner-gantt-view-btn"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Gantt View
            </Button>

            {/* View Type Toggle */}
            <div className="flex border border-slate-300 rounded-md overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewType('project')}
                className={`rounded-none px-3 h-9 ${viewType === 'project' ? 'bg-slate-900 text-white hover:bg-slate-800' : 'hover:bg-slate-50'}`}
                data-testid="view-project"
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Projects
              </Button>
            </div>

            {/* Actual/Forecast Toggle */}
            <div className="flex border border-slate-300 rounded-md overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPlanMode('actual'); setProjectStatusFilter('Secured'); }}
                className={`rounded-none px-3 h-9 ${planMode === 'actual' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'hover:bg-slate-50'}`}
                data-testid="mode-actual"
              >
                Actual
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPlanMode('forecast'); setProjectStatusFilter('forecast'); }}
                className={`rounded-none px-3 h-9 ${planMode === 'forecast' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' : 'hover:bg-slate-50'}`}
                data-testid="mode-forecast"
              >
                Forecast
              </Button>
            </div>

            {/* Time Scale */}
            <div className="flex border border-slate-300 rounded-md overflow-hidden">
              <Button variant="ghost" size="sm" onClick={() => setTimeScale('daily')} className={`rounded-none px-3 h-9 text-xs ${timeScale === 'daily' ? 'bg-slate-200' : 'hover:bg-slate-50'}`}>Day</Button>
              <Button variant="ghost" size="sm" onClick={() => setTimeScale('weekly')} className={`rounded-none px-3 h-9 text-xs ${timeScale === 'weekly' ? 'bg-slate-200' : 'hover:bg-slate-50'}`}>Week</Button>
              <Button variant="ghost" size="sm" onClick={() => setTimeScale('monthly')} className={`rounded-none px-3 h-9 text-xs ${timeScale === 'monthly' ? 'bg-slate-200' : 'hover:bg-slate-50'}`}>Month</Button>
            </div>

            {/* Full Screen */}
            <Button variant="outline" size="sm" onClick={toggleFullScreen} className="h-9" data-testid="fullscreen-toggle">
              {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Conflict Alert Banner */}
      {conflicts.length > 0 && (
        <div className="mx-6 md:mx-8 lg:mx-12 mb-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="eq-conflict-banner">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-700 font-medium">
            {conflicts.length} equipment double-booking{conflicts.length > 1 ? 's' : ''} detected — overlapping project allocations found
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConflictsModal(true)}
            className="ml-auto h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
            data-testid="view-conflicts-btn"
          >
            View Conflicts
          </Button>
        </div>
      )}

      {/* Main Content - Three Panel Layout */}
      <div className={`flex ${isFullScreen ? 'h-[calc(100vh-80px)]' : 'h-[calc(100vh-220px)]'} ${isFullScreen ? 'px-6 pb-4' : 'px-6 md:px-8 lg:px-12 pb-6'}`}>
        
        {/* LEFT PANEL - Resource List */}
        <div className="w-64 flex-shrink-0 bg-white border border-slate-200 rounded-l-lg overflow-hidden flex flex-col" data-testid="left-panel">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tag / serial..."
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="equipment-filter"
              />
            </div>
            <div className="grid grid-cols-2 gap-1.5 mt-2">
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setSubcategoryFilter('all'); }}
                className="h-7 rounded border border-slate-300 bg-white px-1.5 text-[11px] text-slate-700"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={subcategoryFilter}
                onChange={(e) => setSubcategoryFilter(e.target.value)}
                className="h-7 rounded border border-slate-300 bg-white px-1.5 text-[11px] text-slate-700"
                data-testid="subcategory-filter"
              >
                <option value="all">All Subcategories</option>
                {subcategoryOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showAvailableOnly}
                onChange={(e) => setShowAvailableOnly(e.target.checked)}
                className="rounded border-slate-300"
              />
              Available only
            </label>
            {/* Category collapse controls */}
            <div className="flex items-center gap-1 mt-2">
              <button
                type="button"
                onClick={() => toggleAllCategories(false)}
                className="text-[10px] text-slate-500 hover:text-slate-700 underline underline-offset-2"
                data-testid="expand-all-categories"
              >
                Expand All
              </button>
              <span className="text-[10px] text-slate-300">|</span>
              <button
                type="button"
                onClick={() => toggleAllCategories(true)}
                className="text-[10px] text-slate-500 hover:text-slate-700 underline underline-offset-2"
                data-testid="collapse-all-categories"
              >
                Collapse All
              </button>
              {selectedEquipmentIds.size > 0 && (
                <>
                  <span className="text-[10px] text-slate-300 ml-auto">|</span>
                  <span className="text-[10px] text-blue-600 font-medium">{selectedEquipmentIds.size} selected</span>
                  <button
                    type="button"
                    onClick={() => setSelectedEquipmentIds(new Set())}
                    className="text-[10px] text-slate-500 hover:text-slate-700"
                  >
                    ✕
                  </button>
                </>
              )}
            </div>
            {/* Column settings */}
            <div className="flex items-center gap-1 mt-2 relative">
              <button
                type="button"
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5"
                data-testid="column-settings-btn"
              >
                <Settings2 className="w-3 h-3" />Columns
              </button>
              {showColumnSettings && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowColumnSettings(false)} />
                  <div className="absolute top-5 left-0 z-30 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-44">
                    <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Show columns (max 2)</div>
                    {COLUMN_OPTIONS.map(opt => (
                      <label key={opt.key} className="flex items-center gap-1.5 py-0.5 text-xs text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={extraColumns.includes(opt.key)} onChange={() => toggleExtraColumn(opt.key)} className="rounded border-slate-300 h-3 w-3" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2">
              {Object.entries(groupedEquipment).map(([category, items]) => (
                <Collapsible key={category} open={!collapsedCategories[category]} onOpenChange={(open) => setCollapsedCategories(prev => ({ ...prev, [category]: !open }))}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded">
                    <span>{category}</span>
                    <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {items.map((eq) => {
                      const isAvailable = isEquipmentAvailable(eq.id);
                      const dotColor = getEquipmentDotColor(eq);
                      const statusTooltip = getEquipmentStatusTooltip(eq);
                      const isMultiSelected = selectedEquipmentIds.has(eq.id);
                      return (
                        <div
                          key={eq.id}
                          draggable={canEdit}
                          onDragStart={(e) => handleDragStart(e, eq)}
                          onDragEnd={handleDragEnd}
                          className={`px-2 py-1.5 text-sm rounded ${canEdit ? 'cursor-grab' : 'cursor-default'} hover:bg-slate-100 flex items-center justify-between group ${!isAvailable ? 'opacity-60' : ''} ${draggedEquipment?.id === eq.id ? 'ring-2 ring-blue-400' : ''} ${isMultiSelected ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                          onClick={(e) => handleEquipmentClick(e, eq)}
                          title={`${eq.internal_tag} - ${isMultiSelected ? 'Selected (Ctrl+click to deselect)' : 'Click to allocate, Ctrl+click to multi-select'}`}
                          data-testid={`equipment-item-${eq.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <GripVertical className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                              <span className="font-medium text-slate-800 text-sm truncate">{eq.internal_tag}</span>
                            </div>
                            {extraColumns.length > 0 && (
                              <div className="flex items-center gap-2 ml-4 mt-0.5">
                                {extraColumns.map(col => (
                                  <span key={col} className="text-[10px] text-slate-400 truncate">{eq[col] || ''}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} title={statusTooltip} />
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </ScrollArea>
          <div className="p-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
            {filteredEquipment.length} items • {filteredEquipment.filter(e => isEquipmentAvailable(e.id)).length} available
          </div>
        </div>

        {/* MIDDLE PANEL - Project Cards with Gantt */}
        <div ref={middlePanelRef} className="flex-1 bg-white border-t border-b border-slate-200 overflow-hidden flex flex-col" data-testid="middle-panel">
          {/* Project Filter Bar */}
          <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 bg-white">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Filter projects..."
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="project-filter"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setProjectStatusFilter('all')}
                className={`h-7 px-2 text-xs ${projectStatusFilter === 'all' ? 'bg-slate-200' : ''}`}
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setProjectStatusFilter('Secured')}
                className={`h-7 px-2 text-xs ${projectStatusFilter === 'Secured' ? 'bg-emerald-100 text-emerald-800' : ''}`}
              >
                Secured
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setProjectStatusFilter('Likely')}
                className={`h-7 px-2 text-xs ${projectStatusFilter === 'Likely' ? 'bg-amber-100 text-amber-800' : ''}`}
              >
                Likely
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setProjectStatusFilter('Prospect')}
                className={`h-7 px-2 text-xs ${projectStatusFilter === 'Prospect' ? 'bg-blue-100 text-blue-800' : ''}`}
              >
                Prospect
              </Button>
            </div>
            <span className="text-xs text-slate-500">{activeProjects.length} projects</span>
          </div>

          {/* Timeline Header */}
          <div className="flex items-center border-b border-slate-200 bg-slate-50 px-3 py-2">
            <Button variant="ghost" size="sm" onClick={() => navigateTime(-1)} className="h-7 w-7 p-0">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 flex">
              {timeline.map((t, idx) => (
                <div key={idx} className="flex-1 text-center border-l border-slate-200 first:border-l-0">
                  <div className="text-xs font-medium text-slate-700">{t.label}</div>
                  <div className="text-xs text-slate-400">{t.subLabel}</div>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigateTime(1)} className="h-7 w-7 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Project Cards */}
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-3">
              {activeProjects.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <LayoutGrid className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No projects match filter</p>
                </div>
              ) : (
                activeProjects.map((project) => {
                  const projectAllocs = getProjectAllocations(project.id);
                  const isExpanded = expandedProjects[project.id];
                  const isSelected = selectedProject?.id === project.id;
                  const isDropTarget = dropTargetProject === project.id;
                  
                  return (
                    <Card 
                      key={project.id}
                      className={`border-l-4 ${getProjectBorderColor(project.status)} ${isSelected ? 'ring-2 ring-slate-400' : ''} ${isDropTarget ? 'ring-2 ring-blue-400 bg-blue-50' : ''} transition-all hover:shadow-md`}
                      onDragOver={(e) => handleDragOver(e, project.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, project)}
                      data-testid={`project-card-${project.id}`}
                    >
                      <Collapsible open={isExpanded} onOpenChange={() => toggleProject(project.id)}>
                        <CardHeader className="py-2 px-3 cursor-pointer hover:bg-slate-50 flex flex-row items-center justify-between" onClick={() => setSelectedProject(project)}>
                          <div className="flex items-center gap-2 flex-1">
                            <button 
                              type="button" 
                              onClick={(e) => { e.stopPropagation(); toggleProject(project.id); }}
                              className="p-1 hover:bg-slate-200 rounded"
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                            </button>
                            <CardTitle className="text-sm font-semibold text-slate-800">{project.name}</CardTitle>
                            <Badge className={`text-xs ${getProjectColor(project.status)} text-white`}>{project.status}</Badge>
                            {isDropTarget && <Badge className="text-xs bg-blue-500 text-white animate-pulse">Drop here</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{projectAllocs.length}</span>
                            {(() => {
                              const s = new Date(project.start_date);
                              const e = new Date(project.end_date);
                              const days = Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
                              return (
                                <span data-testid={`project-group-duration-${project.id}`}>
                                  {formatDisplayDate(project.start_date)} - {formatDisplayDate(project.end_date)}
                                  <span className="ml-1.5 text-slate-400">({days} Day{days === 1 ? '' : 's'})</span>
                                </span>
                              );
                            })()}
                          </div>
                        </CardHeader>
                        <CollapsibleContent>
                          <CardContent className="pt-0 pb-2 px-3" onClick={() => setSelectedProject(project)}>
                            {/* Gantt Timeline Area */}
                            <div 
                              ref={ganttContainerRef}
                              className={`relative min-h-[60px] rounded border ${isDropTarget ? 'bg-blue-100 border-blue-300' : 'bg-slate-50 border-slate-200'} cursor-pointer transition-colors ${resizing || sliding ? 'select-none' : ''}`}
                            >
                              {/* Timeline grid lines */}
                              <div className="absolute inset-0 flex">
                                {timeline.map((_, idx) => (
                                  <div key={idx} className="flex-1 border-l border-slate-200 first:border-l-0" />
                                ))}
                              </div>
                              
                              {/* Allocation bars */}
                              <div className="relative p-2 space-y-1">
                                {projectAllocs.length === 0 ? (
                                  <div className="text-center py-4 text-xs text-slate-400">
                                    No equipment allocated. Drag equipment from left panel to add.
                                  </div>
                                ) : (
                                  projectAllocs.map((alloc) => {
                                    const eq = getEquipmentById(alloc.equipment_id);
                                    const barStyle = getBarStyle(alloc.start_date, alloc.end_date);
                                    if (!barStyle || !eq) return null;
                                    
                                    const maintenanceOverlay = getMaintenanceOverlay(eq, alloc.start_date, alloc.end_date);
                                    const isResizingThis = resizing?.allocationId === alloc.id;
                                    const isSlidingThis = sliding?.allocationId === alloc.id;
                                    
                                    return (
                                      <div key={alloc.id} className="relative h-7">
                                        {/* Main allocation bar */}
                                        <div
                                          className={`absolute h-6 ${getProjectColor(project.status)} rounded flex items-center justify-between text-xs text-white font-medium shadow-sm group ${isResizingThis || isSlidingThis ? 'ring-2 ring-blue-400' : 'hover:opacity-90'} ${sliding ? 'cursor-grabbing' : 'cursor-grab'}`}
                                          style={{...barStyle, ...getProjectBarStyle(project.status)}}
                                          onMouseDown={(e) => {
                                            // Only slide if not clicking resize handles
                                            if (!e.target.closest('[data-resize-handle]')) {
                                              handleSlideStart(e, alloc.id);
                                            }
                                          }}
                                          onClick={(e) => handleBarClick(e, eq, alloc)}
                                          title={`${eq.internal_tag}: ${formatDisplayDate(alloc.start_date)} - ${formatDisplayDate(alloc.end_date)}${eq.next_maintenance_date ? `\nMaintenance: ${formatDisplayDate(eq.next_maintenance_date)}` : ''}\n\nDrag to slide • Drag edges to resize`}
                                          data-testid={`allocation-bar-${alloc.id}`}
                                        >
                                          {/* Left resize handle */}
                                          {canEdit && (
                                          <div
                                            data-resize-handle="true"
                                            className="absolute left-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover:opacity-100 rounded-l transition-opacity z-20 hover:bg-white/60"
                                            onMouseDown={(e) => handleResizeStart(e, alloc.id, 'start', alloc.start_date)}
                                            title="Drag to change start date"
                                          />
                                          )}
                                          
                                          {/* Bar content */}
                                          <span className="truncate px-4 z-10">{eq.internal_tag}</span>
                                          
                                          {/* Split button */}
                                          {canEdit && (
                                          <button
                                            data-resize-handle="true"
                                            className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-white/30"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const mid = new Date((new Date(alloc.start_date).getTime() + new Date(alloc.end_date).getTime()) / 2);
                                              setSplitModal({ allocationId: alloc.id, equipmentItem: eq, project, splitDate: formatDateForInput(mid) });
                                            }}
                                            title="Split allocation"
                                            data-testid={`split-bar-${alloc.id}`}
                                          >
                                            <Scissors className="w-3 h-3 text-white drop-shadow" />
                                          </button>
                                          )}
                                          {/* Delete button */}
                                          {canEdit && (
                                          <button
                                            data-resize-handle="true"
                                            className="absolute top-0 right-4 h-full w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-red-500/50 rounded"
                                            onClick={(e) => handleQuickDeleteAllocation(alloc.id, e)}
                                            title="Remove allocation"
                                            data-testid={`delete-bar-${alloc.id}`}
                                          >
                                            <X className="w-3 h-3 text-white drop-shadow" />
                                          </button>
                                          )}
                                          
                                          {/* Maintenance overlay (orange indicator) */}
                                          {maintenanceOverlay && (
                                            <div
                                              className="absolute top-0 h-full bg-orange-500 rounded opacity-90 flex items-center justify-center pointer-events-none"
                                              style={maintenanceOverlay}
                                              title={`Maintenance due: ${formatDisplayDate(eq.next_maintenance_date)}`}
                                            >
                                              <Wrench className="w-3 h-3 text-white" />
                                            </div>
                                          )}
                                          
                                          {/* Right resize handle */}
                                          {canEdit && (
                                          <div
                                            data-resize-handle="true"
                                            className="absolute right-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover:opacity-100 rounded-r transition-opacity z-20 hover:bg-white/60"
                                            onMouseDown={(e) => handleResizeStart(e, alloc.id, 'end', alloc.end_date)}
                                            title="Drag to change end date"
                                          />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT PANEL - Project Details */}
        <div className="w-80 flex-shrink-0 bg-white border border-slate-200 rounded-r-lg overflow-hidden flex flex-col" data-testid="right-panel">
          {selectedProject ? (
            <>
              <div className={`p-3 border-b border-slate-200 ${getProjectColor(selectedProject.status)}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-white truncate">{selectedProject.name}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)} className="h-6 w-6 p-0 text-white/80 hover:text-white hover:bg-white/20">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <Badge className="bg-white/20 text-white text-xs">{selectedProject.status}</Badge>
                  <Button
                    size="sm"
                    onClick={() => setDashboardProjectId(selectedProject.id)}
                    data-testid="planner-project-dashboard-btn"
                    className="h-7 px-2.5 bg-white/20 hover:bg-white/30 text-white border border-white/30 text-xs gap-1.5"
                  >
                    <Gauge className="w-3.5 h-3.5" /> Dashboard
                  </Button>
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-4">
                  {/* Project Period */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Period</Label>
                    <p className="text-sm text-slate-800 flex items-center gap-1 mt-1">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {formatDisplayDate(selectedProject.start_date)} - {formatDisplayDate(selectedProject.end_date)}
                    </p>
                  </div>

                  {/* ACTUAL VS FORECAST SUMMARY */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <Label className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-1 mb-3">
                      <Truck className="w-3 h-3" />
                      Equipment: Actual vs Forecast
                    </Label>
                    {(() => {
                      const projectAllocs = getProjectAllocations(selectedProject.id);
                      const forecasts = selectedProject.equipment_forecasts || [];
                      const totalForecast = forecasts.reduce((sum, f) => sum + (f.quantity || 0), 0);
                      const totalActual = projectAllocs.length;
                      const variance = totalActual - totalForecast;
                      
                      return (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Forecasted</span>
                            <span className="text-lg font-bold font-mono text-slate-700">{totalForecast}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Allocated</span>
                            <span className="text-lg font-bold font-mono text-slate-800">{totalActual}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                            <span className="text-xs font-semibold text-slate-600">Variance</span>
                            <Badge className={`text-sm font-mono ${variance < 0 ? 'bg-red-100 text-red-700' : variance > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {variance > 0 ? '+' : ''}{variance}
                            </Badge>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2">
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all ${totalForecast > 0 && totalActual >= totalForecast ? 'bg-emerald-500' : totalActual > 0 ? 'bg-amber-500' : 'bg-slate-300'}`}
                                style={{ width: `${totalForecast > 0 ? Math.min(100, (totalActual / totalForecast) * 100) : 0}%` }}
                              />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1 text-center">
                              {totalForecast > 0 ? `${Math.round((totalActual / totalForecast) * 100)}% allocated` : 'No forecast set'}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Budget */}
                  {selectedProject.equipment_budget && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Equipment Budget</Label>
                      <p className="text-sm text-slate-800 flex items-center gap-1 mt-1">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{formatCurrency(selectedProject.equipment_budget)}</span>
                      </p>
                    </div>
                  )}

                  {/* Forecast by Category */}
                  {selectedProject.equipment_forecasts?.length > 0 && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Forecast by Category</Label>
                      <div className="mt-2 space-y-1">
                        {selectedProject.equipment_forecasts.map((f, idx) => {
                          const actualCount = getProjectAllocations(selectedProject.id).filter(a => {
                            const eq = getEquipmentById(a.equipment_id);
                            return eq?.category === f.category;
                          }).length;
                          const variance = actualCount - (f.quantity || 0);
                          return (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                              <div>
                                <span className="font-medium text-slate-800">{f.category}</span>
                                {f.subcategory && <span className="text-xs text-slate-500 ml-1">/ {f.subcategory}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">{actualCount} / {f.quantity}</span>
                                <Badge className={`text-xs ${variance < 0 ? 'bg-red-100 text-red-700' : variance > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {variance > 0 ? '+' : ''}{variance}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Allocated Equipment List */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Allocated Equipment ({getProjectAllocations(selectedProject.id).length})</Label>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {getProjectAllocations(selectedProject.id).length === 0 ? (
                        <p className="text-xs text-slate-400">No equipment allocated</p>
                      ) : (
                        getProjectAllocations(selectedProject.id).map((alloc) => {
                          const eq = getEquipmentById(alloc.equipment_id);
                          if (!eq) return null;
                          return (
                            <div 
                              key={alloc.id}
                              className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm cursor-pointer hover:bg-slate-100"
                              onClick={() => handleEditAllocation(eq, alloc)}
                            >
                              <div>
                                <span className="font-medium text-slate-800">{eq.internal_tag}</span>
                                <span className="text-xs text-slate-500 ml-1">{eq.category}</span>
                              </div>
                              <span className="text-xs text-slate-500">{formatDisplayDate(alloc.start_date)} - {formatDisplayDate(alloc.end_date)}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Alerts */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Alerts</Label>
                    <div className="mt-2">
                      {(() => {
                        const alerts = [];
                        getProjectAllocations(selectedProject.id).forEach(alloc => {
                          const eq = getEquipmentById(alloc.equipment_id);
                          if (eq?.next_maintenance_date) {
                            const maintDate = new Date(eq.next_maintenance_date);
                            const allocEnd = new Date(alloc.end_date);
                            if (maintDate <= allocEnd) {
                              alerts.push({ type: 'warning', message: `${eq.internal_tag} maintenance due during allocation` });
                            }
                          }
                        });
                        if (alerts.length === 0) {
                          return <p className="text-xs text-slate-400">No alerts</p>;
                        }
                        return alerts.map((alert, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-2 bg-yellow-50 rounded text-sm text-yellow-800 mb-1">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span className="text-xs">{alert.message}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Description */}
                  {selectedProject.description && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase">Description</Label>
                      <p className="text-sm text-slate-600 mt-1">{selectedProject.description}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <LayoutGrid className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Select a project to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Allocation Edit Sheet */}
      <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md" data-testid="allocation-sheet">
          <SheetHeader>
            <SheetTitle>{allocationForm.allocation_id ? 'Edit Allocation' : 'New Allocation'}</SheetTitle>
            <SheetDescription>
              {selectedEquipment?.internal_tag} ({selectedEquipment?.category})
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSaveAllocation} className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project_id">Project *</Label>
              <select
                id="project_id"
                value={allocationForm.project_id}
                onChange={(e) => setAllocationForm({ ...allocationForm, project_id: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                required
              >
                <option value="">Select Project</option>
                {activeProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={allocationForm.start_date}
                  onChange={(e) => setAllocationForm({ ...allocationForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={allocationForm.end_date}
                  onChange={(e) => setAllocationForm({ ...allocationForm, end_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={allocationForm.status}
                onChange={(e) => setAllocationForm({ ...allocationForm, status: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="active">Active</option>
                <option value="planned">Planned</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </form>
          <SheetFooter className="flex gap-2">
            {allocationForm.allocation_id && (
              <Button type="button" variant="destructive" onClick={handleDeleteAllocation}>
                Remove
              </Button>
            )}
            <SheetClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </SheetClose>
            <Button type="submit" onClick={handleSaveAllocation} className="bg-slate-900 hover:bg-slate-800">
              {allocationForm.allocation_id ? 'Update' : 'Create'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <EquipmentGanttView open={showGanttView} onOpenChange={setShowGanttView} />

      {/* Bulk-allocate action bar (appears when items are multi-selected) */}
      {canEdit && (
      <BulkAllocateBar
        count={selectedEquipmentIds.size}
        testidPrefix="eq"
        onAllocate={() => bulk.openForSelected(selectedEquipmentIds, selectedProject)}
        onClear={() => setSelectedEquipmentIds(new Set())}
      />
      )}

      <ProjectDashboard
        projectId={dashboardProjectId}
        open={!!dashboardProjectId}
        onOpenChange={(o) => { if (!o) setDashboardProjectId(null); }}
      />

      {/* Conflicts Modal */}
      {showConflictsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowConflictsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="conflicts-modal">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Equipment Double-Bookings</h3>
                <p className="text-sm text-slate-500">{conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} detected</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowConflictsModal(false)} className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {conflicts.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No conflicts detected</p>
              ) : (
                <div className="space-y-3">
                  {conflicts.map((c, idx) => {
                    const eq = equipment.find(e => e.id === c.resource_id);
                    const proj1 = projects.find(p => p.id === c.allocation_1_project);
                    const proj2 = projects.find(p => p.id === c.allocation_2_project);
                    return (
                      <div key={idx} className="border border-red-200 rounded-lg p-4 bg-red-50/50">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="font-semibold text-slate-800">{eq?.internal_tag || c.resource_id.slice(0, 8)}</span>
                          {eq && <span className="text-xs text-slate-500">{eq.category} / {eq.subcategory}</span>}
                        </div>
                        <div className="grid grid-cols-2 gap-3 ml-6">
                          <div className="bg-white rounded p-2.5 border border-slate-200">
                            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 1</div>
                            <div className="text-sm font-medium text-slate-800">{proj1?.name || 'Unknown'}</div>
                          </div>
                          <div className="bg-white rounded p-2.5 border border-slate-200">
                            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 2</div>
                            <div className="text-sm font-medium text-slate-800">{proj2?.name || 'Unknown'}</div>
                          </div>
                        </div>
                        <div className="ml-6 mt-2 flex items-center gap-2 text-xs text-red-700 bg-red-100 rounded px-2.5 py-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Overlap: <strong>{formatDisplayDate(c.overlap_start)}</strong> — <strong>{formatDisplayDate(c.overlap_end)}</strong></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Allocation Modal */}
      <BatchAllocateModal
        modal={bulk.modal}
        setModal={bulk.setModal}
        projects={projects}
        onConfirm={bulk.handleConfirm}
        entityLabel="Equipment"
        itemNoun="items"
        itemNounCap="Items"
      />

      {/* Split Allocation Modal */}
      {splitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSplitModal(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="split-modal">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Split Allocation</h3>
            <p className="text-sm text-slate-500 mb-4">
              Split <span className="font-medium text-slate-700">{splitModal.equipmentItem?.internal_tag}</span> on <span className="font-medium text-slate-700">{splitModal.project?.name}</span> into two parts
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Split at date</Label>
                <Input
                  type="date"
                  value={splitModal.splitDate}
                  onChange={(e) => setSplitModal({ ...splitModal, splitDate: e.target.value })}
                  data-testid="split-date-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">First half ends day before, second half starts on this date</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setSplitModal(null)}>Cancel</Button>
                <Button size="sm" onClick={handleSplitAllocation} data-testid="confirm-split-button" className="bg-slate-900 hover:bg-slate-800">
                  <Scissors className="w-3.5 h-3.5 mr-1.5" />Split
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
